/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import {
  stroot,
  userActions as userActionsAPI,
} from '@birdi/js-sdk';
import { timeAgo } from '../utils';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { useMetaSelector } from '../../meta/reducer';
import { hasPermission } from '@birdi/utils/src/helpers';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const linkStyle = (theme: BirdiTheme) => css`
  color: ${theme.midnight};
  text-decoration: underline;
`;

const jobNameStyle = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function getActionInfo(action: any) {
  const mapId = action.actionData.mapId;
  const folderId = action.actionData.folderId;

  const mapUrl = mapId ? `/map/next/${mapId}` : '#';

  // => don't track user actions for old missions
  // const missionId = action.actionData.missionId;
  // const missionUrl = missionId ? `/map/missions/${missionId}` : '#';
  // Link => to={mapId ? mapUrl : (missionId ? missionUrl : '#')}

  const userName = `${action.user.fname} ${action.user.lname}`;
  const invitedUserName =
    action.invitedUserName !== ' '
      ? action.invitedUserName
      : action.actionData.invitedUserName || 'a new user';

  const mapNameBeforeDelete = action.actionData?.mapName || '-';
  const folderNameBeforeDelete = action.actionData?.folderName || '-';

  const isMapDeleted = action.mapName ? false : true;
  const isFolderDeleted = action.folderName ? false : true;

  const jobName = action.jobName || '-';

  const onMapClick = (mapId: string) => {
    if (mapId) {
      posthog?.capture('dashboard team activity clicked', { mapId });
    }
  };

  const processingInfo = (verb: string, requesterName: string) => (
    <div>
      Processing {verb}
      {isMapDeleted ? (
        ':'
      ) : (
        <>
          {' '}
          on{' '}
          <Link to={mapUrl} css={linkStyle} onClick={() => onMapClick(mapId)}>
            <strong>{action.mapName}</strong>
          </Link>
          :
        </>
      )}
      <strong css={jobNameStyle} title={jobName}>
        {jobName}
      </strong>
      requested by {requesterName}
    </div>
  );

  switch (action.actionName) {
    case 'create_a_map':
      return (
        <div>
          {userName} created a map:
          <br />
          <Link to={mapUrl} css={linkStyle} onClick={() => onMapClick(mapId)}>
            <strong>Untitled</strong>
          </Link>
        </div>
      );
    case 'share_a_map':
      return (
        <div>
          {userName} shared a map:
          <br />
          {isMapDeleted ? (
            <strong>{mapNameBeforeDelete}</strong>
          ) : (
            <Link to={mapUrl} css={linkStyle} onClick={() => onMapClick(mapId)}>
              <strong>{action.mapName}</strong>
            </Link>
          )}
        </div>
      );
    case 'invite_a_user':
      return (
        <div>
          {userName} invited {invitedUserName} to{' '}
          <strong>{action.workspaceName}</strong>
        </div>
      );
    case 'upload_to_a_map':
      return (
        <div>
          {userName} uploaded to:
          <br />
          {isMapDeleted ? (
            <strong>{mapNameBeforeDelete}</strong>
          ) : (
            <Link to={mapUrl} css={linkStyle} onClick={() => onMapClick(mapId)}>
              <strong>{action.mapName}</strong>
            </Link>
          )}
        </div>
      );
    case 'rename_a_map':
      return (
        <div>
          {userName} renamed {action.actionData.oldMapName} to&nbsp;
          {isMapDeleted ? (
            <strong>{action.actionData.newMapName}</strong>
          ) : (
            <Link to={mapUrl} css={linkStyle} onClick={() => onMapClick(mapId)}>
              <strong>{action.actionData.newMapName}</strong>
            </Link>
          )}
        </div>
      );
    case 'processing_requested':
      return processingInfo('started', userName);
    case 'processing_completed':
      return processingInfo('completed', action.jobRequesterName);
    case 'create_a_folder':
      return (
        <div>
          {userName} has created a new folder:
          <br />
          {isFolderDeleted ? (
            <strong>{folderNameBeforeDelete}</strong>
          ) : (
            <Link to={`/new-maps/folder/${folderId}`} css={linkStyle}>
              <strong>{action.folderName}</strong>
            </Link>
          )}
        </div>
      );
    case 'move_map_to_a_folder':
      return (
        <div>
          {userName} has moved&nbsp;
          {isMapDeleted ? (
            <strong>{mapNameBeforeDelete}</strong>
          ) : (
            <Link to={mapUrl} css={linkStyle} onClick={() => onMapClick(mapId)}>
              <strong>{action.mapName}</strong>
            </Link>
          )}
          &nbsp;to the&nbsp;
          {isFolderDeleted ? (
            <strong>{folderNameBeforeDelete}</strong>
          ) : (
            <Link to={`/new-maps/folder/${folderId}`} css={linkStyle}>
              <strong>{action.folderName}</strong>
            </Link>
          )}
          &nbsp;folder
        </div>
      );
    case 'delete_a_map':
      return (
        <div>
          {userName} deleted map{' '}
          <strong>{action.actionData.deletedMapName}</strong>
        </div>
      );
    default:
      return <div>{userName} did something!</div>;
  }
}

export const TeamActivity: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [actions, setActions] = useState<Array<any>>([]);
  const [start, setStart] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const auth = useSelector((state) => state.auth);
  const meta = useMetaSelector();
  const canInvite = hasPermission(meta.permissions, 'workspace:user:create');

  const loadList = (reset = false) => {
    setLoading(true);
    userActionsAPI
      .getUserActions(stroot('va1oon'), { start: reset ? 0 : start })
      .then((res: any) => {
        const newActions = res?.body?.userActions || [];
        const totalCount = res?.body?.totalCount || 0;
        if (reset) {
          setActions(newActions);
          setStart(newActions.length);
        } else {
          setActions((prev) => [...prev, ...newActions]);
          setStart((prev) => prev + newActions.length);
        }
        setHasMore(
          (reset ? 0 : actions.length) + newActions.length < totalCount,
        );
        setLoading(false);
      });
  };

  // Initial fetch
  useEffect(() => {
    loadList(true);
  }, [meta.organisation?.id]);

  // Fetch next page
  const handleExpand = () => {
    posthog?.capture('dashboard team activity expand clicked');
    loadList();
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
      id={suppressIntercomAnchor ? undefined : 'team-activity'}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Team activity</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
          `}
        >
          Recent collaborations and team updates
        </div>
        {actions.length === 0 && !loading && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div>
              <strong>Team activity will show up here:</strong> See when team
              members upload, share, or process data. Invite teammates to start
              collaborating.
            </div>
            <button
              onClick={() => navigate('/admin-users?modal=invite')}
              css={css`
                color: ${theme.deepOcean};
                text-decoration: underline;
                background: none;
                border: none;
                padding: 0;
                font: inherit;
                cursor: pointer;
                margin-top: 0.5em;
              `}
              disabled={auth.status !== 'authenticated' || !canInvite}
            >
              + Invite a team member
            </button>
          </div>
        )}
        {actions.length > 0 && (
          <div>
            {actions.map((action, index) => (
              <div
                key={action.id}
                css={css`
                  display: flex;
                  align-items: center;
                  border-radius: 1px;
                  padding: 0.5em;
                  border-bottom: solid
                    ${index === actions.length - 1 ? 'none' : '1px'}
                    ${theme.mono20};
                  background: transparent;
                  transition: background 0.15s;
                  &:hover {
                    background: ${theme.mono20};
                  }
                `}
              >
                <div
                  css={css`
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: ${theme.lightOcean};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 1.1em;
                    margin-right: 1em;
                    overflow: hidden;
                  `}
                >
                  {action.user.profile_pic ? (
                    <img
                      src={action.user.profile_pic}
                      alt="avatar"
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <span
                      css={css`
                        color: ${theme.deepOcean};
                      `}
                    >
                      {action.user.fname[0]}
                      {action.user.lname[0]}
                    </span>
                  )}
                </div>
                <div
                  css={css`
                    flex: 1;
                    /* Without this the text can't shrink below its longest
                       word, which pushed the timestamp out and forced names
                       to break mid-word when the column got narrow. */
                    min-width: 0;
                  `}
                >
                  <span
                    css={css`
                      color: ${theme.midnight};
                    `}
                  >
                    {getActionInfo(action)}
                  </span>
                </div>
                <div
                  css={css`
                    color: ${theme.mono60};
                    font-size: 0.95em;
                    text-align: right;
                    /* Give the text its space back: the timestamp is short and
                       fixed, so let it size to content and never wrap rather
                       than reserving 90px of a narrow column. */
                    flex: 0 0 auto;
                    white-space: nowrap;
                    padding-left: 0.5em;
                  `}
                >
                  {timeAgo(action.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}
        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>
      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          disabled={loading || !hasMore}
          onClick={handleExpand}
          css={css`
            width: 100%;
            color: ${loading || !hasMore ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: ${loading || !hasMore ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
          `}
        >
          Expand
        </button>
      </div>
    </div>
  );
};
